import React, { useState } from 'react'
import { PageData } from '@/api/Pagination/Types'
import {
  RewardMetric,
  RewardProgramDetailsDto,
  RewardProgramDetailsUpdateDto,
  RewardProgramTier,
} from '@/api/Trading-Accounts/Types/RewardProgramDto'
import { RewardProgramDto, RewardsHubDto } from '@/api/Trading-Accounts/Types/RewardsHubDto'
import { DataCard, DataGrid } from '@/components/DataCard/DataCard'
import { FormBaseErrorMessage } from '@/components/Form/Form.styles'
import {
  FormDateField,
  FormField,
  FormFieldLayout,
  FormMultiSelectField,
  FormSelectField,
} from '@/components/Form/FormField'
import { Select } from '@/components/Select'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { DisableRewardProgramModal } from '@/routes/Marketing/RewardsHub/RewardPrograms/DisableRewardProgramModal'
import { validationSchema } from '@/routes/Marketing/RewardsHub/RewardPrograms/RewardProgramsValidationSchema'
import { useRewardProgramOptions } from '@/routes/Marketing/RewardsHub/RewardPrograms/useRewardProgramOptions'
import { FormSubmitValues } from '@/utils/formValidation'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'

import styles from './RewardProgramEditForm.module.scss'

interface OuterProps {
  initialValues: RewardProgramDetailsDto
  onSubmit(values: FormSubmitValues<RewardProgramDetailsUpdateDto>): Promise<void>
  rewardsList?: PageData<RewardsHubDto[]>
  rewardProgramList?: PageData<RewardProgramDto[]>
}

export const RewardProgramEditFormUI: React.FC<
  OuterProps & FormikProps<RewardProgramDetailsDto>
> = (props) => {
  const { values, setFieldValue, isValid, errors } = props
  const [isEdit, setEdit] = useState<boolean>(false)
  const [isOpenDisableProgramModal, setIsOpenDisableProgramModal] = useState(false)

  const {
    statusOptions,
    currencyOptions,
    companyOptions,
    countryOptions,
    metricsOptions,
    conditionalLogicOptions,
    rewardsOptions,
    rewardCadenceOptions,
  } = useRewardProgramOptions(props.rewardsList)

  const handleSubmitForm = () => {
    setEdit(false)
    props.handleSubmit()
  }

  const newMetric = () => ({
    trackingMetric: { id: 0 },
    rewardProgramTierConditionLogic: { id: 0 },
    rewardProgramTierLogicalOperator: { id: 1 },
    value: 0,
  })

  const newTier = () => ({
    name: '',
    trackingMetric: { id: 0 },
    rewardProgramTierConditionLogic: { id: 0 },
    reward: { id: 0 },
    callToActionText: '',
    metrics: [],
    currency: { id: 0 },
  })

  const handleDisableProgram = async () => {
    const payload: RewardProgramDetailsUpdateDto = {
      id: values.id,
      internalName: values.internalName,
      displayName: values.displayName,
      description: values.description,
      displayOrder: values.displayOrder,
      numberOfTiers: values.numberOfTiers,
      lastModified: values.lastModified,
      startDate: values.startDate,
      endDate: values.endDate,
      rewardStatusId: 2,
      resetCadenceId: values.resetCadence?.id,
      companies: values.companies?.map((c) => c.id),
      countries: values.countries?.map((c) => c.id),
      tiers: values.tiers?.map((tier) => ({
        id: tier.id,
        tierNumber: tier.tierNumber,
        name: tier.name,
        thresholdValue: tier.thresholdValue,
        callToActionText: tier.callToActionText,
        currencyId: tier.trackingMetric.id === 1 ? null : tier.currency?.id,
        trackingMetricId: tier.trackingMetric?.id,
        rewardProgramTierConditionLogicId: tier.rewardProgramTierConditionLogic?.id,
        rewardId: tier.reward.id,
        metrics: (tier.metrics ?? []).map((metric) => ({
          id: metric.id,
          value: metric.value,
          currencyId: metric.trackingMetric?.id === 1 ? null : metric.currency?.id,
          rewardProgramTierLogicalOperatorId: metric.rewardProgramTierLogicalOperator?.id,
          rewardProgramTierConditionLogicId: metric.rewardProgramTierConditionLogic?.id,
          trackingMetricId: metric.trackingMetric?.id,
        })),
      })),
    }

    await props.onSubmit(payload)
    setIsOpenDisableProgramModal(false)
  }

  const handleAddTier = () => {
    const updated = [...(values.tiers ?? []), newTier()]
    setFieldValue('tiers', updated)
    setFieldValue('numberOfTiers', updated.length)
  }

  const handleRemoveTier = (i: number) => {
    const updated = (values.tiers ?? []).filter((_, idx) => idx !== i)
    setFieldValue('tiers', updated)
    setFieldValue('numberOfTiers', updated.length)
  }

  const getConditionalLogicOptions = (trackingMetricId: number) => {
    if (trackingMetricId === 1) {
      // Trading Volume — only show one option, e.g. id: 1 ("Greater Than" or whichever applies)
      return conditionalLogicOptions.filter((option) => option.id === 1)
    }
    return conditionalLogicOptions
  }

  const handleAddCondition = (tierIndex: number) => {
    const current = values.tiers?.[tierIndex]?.metrics ?? []
    const sharedCurrency = current[0]?.currency ?? { id: 0 }
    const sharedOperator = current[0]?.rewardProgramTierLogicalOperator ?? { id: 1 }
    setFieldValue(`tiers.${tierIndex}.metrics`, [
      ...current,
      {
        ...newMetric(),
        currency: sharedCurrency,
        rewardProgramTierLogicalOperator: sharedOperator,
      },
    ])
  }

  const handleRemoveCondition = (tierIndex: number, metricIndex: number) => {
    const current = values.tiers?.[tierIndex]?.metrics ?? []
    setFieldValue(
      `tiers.${tierIndex}.metrics`,
      current.filter((_, i) => i !== metricIndex)
    )
  }

  const typedErrors = errors as FormikErrors<RewardProgramDetailsDto> & {
    tiers?: FormikErrors<RewardProgramTier>[]
  }

  const getTierError = (index: number) => {
    const tierErrors = typedErrors.tiers?.[index]
    if (!tierErrors || typeof tierErrors === 'string') return {}
    return tierErrors as FormikErrors<RewardProgramTier>
  }

  const getMetricError = (tierIndex: number, metricIndex: number) => {
    const tierErr = getTierError(tierIndex)
    const metricErrors = (tierErr.metrics as FormikErrors<RewardMetric>[] | undefined)?.[
      metricIndex
    ]
    if (!metricErrors || typeof metricErrors === 'string') return {}
    return metricErrors as FormikErrors<RewardMetric>
  }
  return (
    <>
      <DisableRewardProgramModal
        isOpen={isOpenDisableProgramModal}
        onClose={() => setIsOpenDisableProgramModal(false)}
        programName={values.internalName}
        onConfirm={handleDisableProgram}
      />
      <Form>
        <div className={styles.form}>
          {!isEdit && (
            <Button
              className='text-xs px-3 py-1 border border-solid border-red-400 rounded bg-transparent cursor-pointer text-red-500'
              disabled={isEdit || values.rewardStatus.id === 2}
              onClick={() => setIsOpenDisableProgramModal(true)}
              variant={'ghost'}
            >
              Disable
            </Button>
          )}
          {!isEdit && (
            <Button disabled={isEdit} type='button' onClick={() => setEdit(true)}>
              Edit
            </Button>
          )}
          {isEdit && (
            <Button
              type='button'
              onClick={() => {
                setEdit(false)
                props.resetForm()
              }}
            >
              Cancel
            </Button>
          )}
          {isEdit && (
            <Button disabled={!isEdit || !isValid} type='button' onClick={handleSubmitForm}>
              Save
            </Button>
          )}
        </div>
        <DataGrid>
          <DataCard title='General Program Settings'>
            <FormField name='internalName' label='Program Name (internal)' readOnly={!isEdit} />
            <FormField name='displayName' label='Display Name (client facing)' readOnly={!isEdit} />
            <FormField name='description' label='Description (client facing)' readOnly={!isEdit} />
            <FormMultiSelectField
              name='companies'
              label='Entities'
              readOnly={!isEdit}
              options={companyOptions}
            />

            <FormMultiSelectField
              name='countries'
              label='Countries'
              readOnly={!isEdit}
              options={countryOptions}
            />

            <FormSelectField
              name='rewardStatus.name'
              label='Program Status'
              readOnly={!isEdit}
              options={statusOptions}
              onChange={(value) => setFieldValue('statusId', Number(value))}
            />
            <FormField name='displayOrder' label='Display Order' readOnly={!isEdit} />
            <FormDateField name='startDate' label='Start Date' readOnly={!isEdit} showTimeSelect />
            <FormDateField name='endDate' label='End Date' readOnly={!isEdit} showTimeSelect />
          </DataCard>
          <DataCard title='Program Logic'>
            <FormSelectField
              name='resetCadence.name'
              label='Reset Cadence'
              readOnly={!isEdit}
              options={rewardCadenceOptions}
              onChange={(value) => setFieldValue('resetCadence.id', Number(value))}
            />
            <FormField name='numberOfTiers' label='Total Tiers' readOnly />
            <FormDateField name='lastModified' label='Last Modified' readOnly showTimeSelect />
          </DataCard>

          <DataCard title='Tier and Reward Configuration'>
            {values.tiers?.map((tier, index) => (
              <DataCard key={index}>
                <FormField
                  name={`tiers.${index}.name`}
                  label={`Tier ${index + 1}`}
                  readOnly={!isEdit}
                />

                <FormFieldLayout label=''>
                  <Select
                    readOnly={!isEdit}
                    value={tier.trackingMetric?.id}
                    className='w-full max-w-48'
                    options={metricsOptions.map((metric) => ({
                      value: metric.id,
                      label: metric.name,
                    }))}
                    onChange={(value) =>
                      setFieldValue(`tiers.${index}.trackingMetric.id`, Number(value))
                    }
                  />

                  <Select
                    name={`tiers.${index}.rewardProgramTierConditionLogic.id`}
                    value={tier.rewardProgramTierConditionLogic?.id}
                    readOnly={!isEdit}
                    className='w-full max-w-28'
                    options={getConditionalLogicOptions(tier.trackingMetric?.id).map((option) => ({
                      value: option.id,
                      label: option.name,
                    }))}
                    onChange={(value) =>
                      setFieldValue(
                        `tiers.${index}.rewardProgramTierConditionLogic.id`,
                        Number(value)
                      )
                    }
                  />
                  <Input
                    className='w-24'
                    name={`tiers.${index}.thresholdValue`}
                    value={tier.thresholdValue}
                    readOnly={!isEdit}
                    onChange={(e) =>
                      setFieldValue(`tiers.${index}.thresholdValue`, Number(e.target.value))
                    }
                  />
                </FormFieldLayout>

                {typedErrors.tiers?.[index]?.trackingMetric?.id && (
                  <FormBaseErrorMessage>
                    {typedErrors.tiers[index].trackingMetric?.id}
                  </FormBaseErrorMessage>
                )}
                {typedErrors.tiers?.[index]?.rewardProgramTierConditionLogic?.id && (
                  <FormBaseErrorMessage>
                    {typedErrors.tiers[index].rewardProgramTierConditionLogic?.id}
                  </FormBaseErrorMessage>
                )}
                {typedErrors.tiers?.[index]?.thresholdValue && (
                  <FormBaseErrorMessage>
                    {typedErrors.tiers[index].thresholdValue}
                  </FormBaseErrorMessage>
                )}

                {tier.metrics.length > 0 && (
                  <FormFieldLayout label='Condition'>
                    {(() => {
                      const activeOperator = tier.metrics[0]?.rewardProgramTierLogicalOperator?.id
                      return (
                        <div className='flex gap-2'>
                          <Button
                            disabled={!isEdit}
                            variant={activeOperator === 1 ? 'destructive' : 'default'}
                            onClick={() =>
                              tier.metrics.forEach((_, i) =>
                                setFieldValue(
                                  `tiers.${index}.metrics.${i}.rewardProgramTierLogicalOperator.id`,
                                  1
                                )
                              )
                            }
                          >
                            AND
                          </Button>
                          <Button
                            disabled={!isEdit}
                            variant={activeOperator === 2 ? 'destructive' : 'default'}
                            onClick={() =>
                              tier.metrics.forEach((_, i) =>
                                setFieldValue(
                                  `tiers.${index}.metrics.${i}.rewardProgramTierLogicalOperator.id`,
                                  2
                                )
                              )
                            }
                          >
                            OR
                          </Button>
                        </div>
                      )
                    })()}
                  </FormFieldLayout>
                )}

                {tier.metrics.length > 0 && (
                  <FormSelectField
                    name={`tiers.${index}.metrics.0.currency.id`}
                    label='Currency'
                    value={tier.metrics[0]?.currency?.id}
                    readOnly={!isEdit}
                    options={currencyOptions}
                    onChange={(value) => {
                      // Apply the same currency to ALL metrics in this tier
                      tier.metrics.forEach((_, metricIndex) => {
                        setFieldValue(`tiers.${index}.metrics.${metricIndex}.currency.id`, value)
                      })
                    }}
                  />
                )}

                {tier.metrics.map((metric, metricIndex) => (
                  <>
                    <FormFieldLayout label='' key={metricIndex}>
                      <Select
                        name={`tiers.${index}.metrics.${metricIndex}.trackingMetric.id`}
                        value={metric.trackingMetric?.id}
                        readOnly={!isEdit}
                        className='w-full max-w-48'
                        options={metricsOptions.map((metric) => ({
                          value: metric.id,
                          label: metric.name,
                        }))}
                        onChange={(value) => {
                          const numValue = Number(value)
                          setFieldValue(
                            `tiers.${index}.metrics.${metricIndex}.trackingMetric.id`,
                            numValue
                          )
                          if (numValue === 1) {
                            setFieldValue(
                              `tiers.${index}.metrics.${metricIndex}.rewardProgramTierConditionLogic.id`,
                              1
                            )
                          }
                        }}
                      />
                      <Select
                        name={`tiers.${index}.metrics.${metricIndex}.rewardProgramTierConditionLogic.id`}
                        value={metric.rewardProgramTierConditionLogic?.id}
                        readOnly={!isEdit}
                        className='w-full max-w-28'
                        options={getConditionalLogicOptions(metric.trackingMetric?.id).map(
                          (option) => ({
                            value: option.id,
                            label: option.name,
                          })
                        )}
                        onChange={(value) =>
                          setFieldValue(
                            `tiers.${index}.metrics.${metricIndex}.rewardProgramTierConditionLogic.id`,
                            Number(value)
                          )
                        }
                      />

                      <Input
                        name={`tiers.${index}.metrics.${metricIndex}.value`}
                        className='w-24'
                        value={metric.value}
                        readOnly={!isEdit}
                        onChange={(e) =>
                          setFieldValue(
                            `tiers.${index}.metrics.${metricIndex}.value`,
                            Number(e.target.value)
                          )
                        }
                      />
                      {isEdit && (
                        <Button
                          className='h-7 p2'
                          onClick={() => handleRemoveCondition(index, metricIndex)}
                        >
                          x
                        </Button>
                      )}
                    </FormFieldLayout>

                    {getMetricError(index, metricIndex).trackingMetric?.id && (
                      <FormBaseErrorMessage>
                        {getMetricError(index, metricIndex).trackingMetric?.id}
                      </FormBaseErrorMessage>
                    )}
                    {getMetricError(index, metricIndex).rewardProgramTierConditionLogic?.id && (
                      <FormBaseErrorMessage>
                        {getMetricError(index, metricIndex).rewardProgramTierConditionLogic?.id}
                      </FormBaseErrorMessage>
                    )}
                    {getMetricError(index, metricIndex).value && (
                      <FormBaseErrorMessage>
                        {getMetricError(index, metricIndex).value}
                      </FormBaseErrorMessage>
                    )}
                    {getMetricError(index, metricIndex).rewardProgramTierLogicalOperator?.id && (
                      <FormBaseErrorMessage>
                        {getMetricError(index, metricIndex).rewardProgramTierLogicalOperator?.id}
                      </FormBaseErrorMessage>
                    )}
                  </>
                ))}

                {/*{getTierError(index).rewardProgramTierConditionLogic?.id && (*/}
                {/*  <FormBaseErrorMessage>*/}
                {/*    {getTierError(index).rewardProgramTierConditionLogic?.id}*/}
                {/*  </FormBaseErrorMessage>*/}
                {/*)}*/}
                {getTierError(index).thresholdValue && (
                  <FormBaseErrorMessage>{getTierError(index).thresholdValue}</FormBaseErrorMessage>
                )}

                {/* Add condition + Remove tier buttons */}
                {isEdit && (
                  <FormFieldLayout label=''>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                        <Tooltip>
                          <TooltipTrigger>
                            <span
                              style={{
                                pointerEvents:
                                  (tier.metrics ?? []).length >= 3 ? 'auto' : undefined,
                              }}
                            >
                              <Button
                                variant='ghost'
                                className='text-xs px-3 py-1 border border-dashed border-gray-400 rounded bg-transparent cursor-pointer'
                                onClick={() => handleAddCondition(index)}
                                disabled={(tier.metrics ?? []).length >= 3}
                              >
                                + Add Condition
                              </Button>
                            </span>
                          </TooltipTrigger>
                          {(tier.metrics ?? []).length >= 3 && (
                            <TooltipContent side='bottom'>
                              Maximum of 3 conditions per tier reached.
                            </TooltipContent>
                          )}
                        </Tooltip>
                        {values.tiers && values.tiers.length > 1 && (
                          <Button
                            variant='ghost'
                            className='text-xs px-3 py-1 border border-dashed border-red-400 rounded bg-transparent cursor-pointer text-red-500'
                            onClick={() => handleRemoveTier(index)}
                          >
                            × Remove Tier {index + 1}
                          </Button>
                        )}
                      </div>
                    </div>
                  </FormFieldLayout>
                )}

                <FormSelectField
                  name={`tierReward_${index}`}
                  value={values.tiers?.[index]?.reward?.id}
                  label='Reward to claim'
                  readOnly={!isEdit}
                  options={rewardsOptions}
                  onChange={(value) => {
                    const selected = rewardsOptions.find((r) => r.id === value)
                    setFieldValue(`tiers.${index}.reward`, { id: value, name: selected?.name })
                  }}
                />

                {getTierError(index).reward?.id && (
                  <FormBaseErrorMessage>{getTierError(index).reward?.id}</FormBaseErrorMessage>
                )}

                <FormField
                  name={`tiers.${index}.callToActionText`}
                  label='Button Text (CTA)'
                  value={tier.callToActionText}
                  readOnly={!isEdit}
                />
              </DataCard>
            ))}

            {isEdit && (
              <div className='flex justify-start'>
                <Tooltip>
                  <TooltipTrigger>
                    <span
                      style={{
                        pointerEvents: (values.tiers ?? []).length >= 5 ? 'auto' : undefined,
                      }}
                    >
                      <Button
                        className='w-24'
                        onClick={handleAddTier}
                        disabled={(values.tiers ?? []).length >= 5}
                      >
                        + Add Tier
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {(values.tiers ?? []).length >= 5 && (
                    <TooltipContent side='bottom'>Maximum of 5 tiers reached</TooltipContent>
                  )}
                </Tooltip>
              </div>
            )}
          </DataCard>
        </DataGrid>
      </Form>
    </>
  )
}

export const RewardProgramEditForm = withFormik<OuterProps, RewardProgramDetailsDto>({
  enableReinitialize: true,
  validateOnMount: true,
  validationSchema: (props: OuterProps) =>
    validationSchema(props.rewardProgramList?.items?.length ?? 0),
  mapPropsToValues: (props) => props.initialValues,
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      const payload: RewardProgramDetailsUpdateDto = {
        id: values.id,
        internalName: values.internalName,
        displayName: values.displayName,
        description: values.description,
        displayOrder: values.displayOrder,
        numberOfTiers: values.numberOfTiers,
        lastModified: values.lastModified,
        startDate: values.startDate,
        endDate: values.endDate,
        rewardStatusId: values.rewardStatus?.id,
        resetCadenceId: values.resetCadence?.id,
        companies: values.companies?.map((company) => company.id),
        countries: values.countries?.map((country) => country.id),
        tiers: values.tiers?.map((tier) => ({
          id: tier.id,
          tierNumber: tier.tierNumber,
          name: tier.name,
          thresholdValue: tier.thresholdValue,
          callToActionText: tier.callToActionText,
          currencyId: tier.trackingMetric.id === 1 ? null : tier.currency?.id,
          trackingMetricId: tier.trackingMetric?.id,
          rewardProgramTierConditionLogicId: tier.rewardProgramTierConditionLogic?.id,
          rewardId: tier.reward.id,
          metrics: (tier.metrics ?? []).map((metric) => ({
            id: metric.id,
            value: metric.value,
            currencyId: metric.trackingMetric?.id === 1 ? null : metric.currency?.id,
            rewardProgramTierLogicalOperatorId: metric.rewardProgramTierLogicalOperator?.id,
            rewardProgramTierConditionLogicId: metric.rewardProgramTierConditionLogic?.id,
            trackingMetricId: metric.trackingMetric?.id,
          })),
        })),
      }
      await props.onSubmit(payload)
    } finally {
      setSubmitting(false)
    }
  },
})(RewardProgramEditFormUI)
